import { useCallback, useRef } from 'react';

import { MOD_CANCER_FOCUS_ID } from '@/constants/medicalOnDemand';
import { useProductSupportPostSubmitAnalytics } from '@/hooks/useProductSupportPostSubmitAnalytics';
import {
  getProductSupportEligibilityV2,
  type ProductSupportEligibilityResult,
  type ProductSupportEligibilitySource,
} from '@/services/getProductSupportEligibilityV2';
import {
  getProductSupportSuccessState,
  type ProductSupportSuccessState,
} from '@/util/productSupportSuccessState';

type PendingEligibility = {
  submissionId: number;
  promise: Promise<ProductSupportEligibilityResult | null>;
};

type CaptureSubmissionParams = {
  message?: string;
  cancerFocusId?: string;
};

type ResolvePostSubmitSuccessParams = {
  inquiryId?: number;
};

type ResolvePostSubmitSuccessResult = {
  submittedCancerFocusId: string;
  successState: ProductSupportSuccessState;
};

type UseProductSupportPostSubmitParams = {
  isModProductSupportSuccessEnabled: boolean;
  source: ProductSupportEligibilitySource;
};

/**
 * Shares MOD post-submit snapshot + eligibility evaluation between inquiry flows.
 */
export function useProductSupportPostSubmit(params: UseProductSupportPostSubmitParams) {
  const { isModProductSupportSuccessEnabled, source } = params;

  const { trackProductSupportEvaluated } = useProductSupportPostSubmitAnalytics();

  const latestSubmittedMessageRef = useRef('');

  const latestSubmittedCancerFocusRef = useRef('');

  const submissionSequenceRef = useRef(0);

  const pendingEligibilityRef = useRef<PendingEligibility | null>(null);

  const captureSubmissionSnapshot = useCallback(
    ({ message, cancerFocusId }: CaptureSubmissionParams) => {
      const trimmedMessage = (message || '').trim();

      const normalizedCancerFocusId = cancerFocusId || '';

      latestSubmittedMessageRef.current = trimmedMessage;

      latestSubmittedCancerFocusRef.current = normalizedCancerFocusId;

      submissionSequenceRef.current += 1;

      if (
        isModProductSupportSuccessEnabled &&
        normalizedCancerFocusId === MOD_CANCER_FOCUS_ID &&
        trimmedMessage.length > 0
      ) {
        const submissionId = submissionSequenceRef.current;

        pendingEligibilityRef.current = {
          submissionId,
          promise: getProductSupportEligibilityV2({
            cancerFocusId: normalizedCancerFocusId,
            message: trimmedMessage,
            source,
          }),
        };

        return;
      }

      pendingEligibilityRef.current = null;
    },
    [isModProductSupportSuccessEnabled, source]
  );

  const resolvePostSubmitSuccess = useCallback(
    async ({
      inquiryId,
    }: ResolvePostSubmitSuccessParams = {}): Promise<ResolvePostSubmitSuccessResult> => {
      const submittedMessage = latestSubmittedMessageRef.current;

      const submittedCancerFocusId = latestSubmittedCancerFocusRef.current;

      const currentSubmissionId = submissionSequenceRef.current;

      const pendingEligibility = pendingEligibilityRef.current;

      const shouldEvaluateProductSupportEligibility =
        isModProductSupportSuccessEnabled &&
        submittedCancerFocusId === MOD_CANCER_FOCUS_ID &&
        submittedMessage.length > 0;

      const hasCurrentEligibilityRequest = pendingEligibility?.submissionId === currentSubmissionId;

      const eligibility =
        shouldEvaluateProductSupportEligibility && hasCurrentEligibilityRequest
          ? await pendingEligibility.promise
          : null;

      let eligibilityRequestStatus: 'not_requested' | 'stale' | 'succeeded' | 'failed' =
        'not_requested';

      if (shouldEvaluateProductSupportEligibility && !hasCurrentEligibilityRequest) {
        eligibilityRequestStatus = 'stale';
      }

      if (shouldEvaluateProductSupportEligibility && hasCurrentEligibilityRequest) {
        eligibilityRequestStatus = eligibility ? 'succeeded' : 'failed';
      }

      trackProductSupportEvaluated({
        source,
        inquiryId,
        requestStatus: eligibilityRequestStatus,
        eligibility,
        cancerFocusId: submittedCancerFocusId,
        messageLength: submittedMessage.length,
      });

      const successState = isModProductSupportSuccessEnabled
        ? getProductSupportSuccessState(eligibility, submittedMessage)
        : { showSuccess: false as const };

      return {
        submittedCancerFocusId,
        successState,
      };
    },
    [isModProductSupportSuccessEnabled, source, trackProductSupportEvaluated]
  );

  return {
    captureSubmissionSnapshot,
    resolvePostSubmitSuccess,
  };
}
